import { thirdPartyErrorFilterIntegration } from "@sentry/browser";
import { ErrorBoundary as SentryErrorBoundary, init as sentryInit, rewriteFramesIntegration } from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { DEBUG_PAGE_REGEX, UI_SENTRY_URL } from "common/constants";
import Flags from "common/constants/flags";
import {
  reloadPageWithNotification,
  shouldAttemptChunkReload,
  showPersistentReloadNotification,
} from "common/utils/chunkReloadGuard";
import { isRenderDebugEnabled, isSentryEnabled } from "common/utils/context";
import { isChunkLoadError } from "common/utils/isChunkLoadError";
import { initDatadogRum } from "lib/datadogRum";
import { PostHogIntegration } from "lib/sentryPosthogIntegration";
import { DEPLOYMENT_ID_MISMATCH, loadClientFetchMiddleware } from "lib/utils/fetchAuthHelpers";
import getPageTitle from "lib/utils/getPageTitle";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { shouldReloadForMismatchDeploymentId } from "./deploymentRefresh";
import "./polyfills/node-globals";
import type { AppRouterContext } from "./routerContext";
import { routeTree } from "./routeTree.gen";

// Load client fetch middleware (intercepts window.fetch for deployment ID mismatch)
void loadClientFetchMiddleware();

// When the server tells us the client is running an outdated deploy, pin a
// reload notification so the user can refresh at a non-disruptive time.
// Fires on the next API call, before the user can trigger a stale dynamic
// import. The guard is a backstop, not the primary dedup.
window.addEventListener(
  DEPLOYMENT_ID_MISMATCH,
  (event: Event) => {
    const deploymentId = event instanceof CustomEvent && event.detail !== undefined ? String(event.detail) : "unknown";

    if (!shouldReloadForMismatchDeploymentId(deploymentId)) {
      console.warn(
        `[client-reload] Ignoring repeated ${DEPLOYMENT_ID_MISMATCH}=${deploymentId} after initial reload attempt.`,
      );
      return;
    }

    if (shouldAttemptChunkReload()) {
      showPersistentReloadNotification(`Received ${DEPLOYMENT_ID_MISMATCH}=${deploymentId}`);
    }
  },
  { once: false },
);

const REACT_SCAN_SCRIPT_SOURCE = "https://unpkg.com/react-scan/dist/auto.global.js";
const REACT_SCAN_SCRIPT_ID = "react-scan-auto";

function ensureReactScanScriptLoaded(): Promise<void> {
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(REACT_SCAN_SCRIPT_ID);
    if (existingScript) {
      resolve();
      return;
    }

    const scriptElement = document.createElement("script");
    scriptElement.id = REACT_SCAN_SCRIPT_ID;
    scriptElement.src = REACT_SCAN_SCRIPT_SOURCE;
    scriptElement.async = false;
    scriptElement.onload = () => {
      resolve();
    };
    scriptElement.onerror = () => {
      reject(new Error(`Failed to load ${REACT_SCAN_SCRIPT_SOURCE}`));
    };
    document.head.appendChild(scriptElement);
  });
}

// Reload the page when Vite fails to fetch a dynamically imported chunk (e.g. after a deployment).
// This fires before React error boundaries, so it covers every lazy component globally.
// Guarded by a timestamped cooldown so the recovery path can fire again later in the
// session if a *new* deploy lands, while still preventing tight reload loops.
window.addEventListener("vite:preloadError", () => {
  if (shouldAttemptChunkReload()) {
    reloadPageWithNotification("Detected vite:preloadError chunk-load failure");
  }
});

// Initialize Datadog RUM
initDatadogRum();

// Initialize Sentry
sentryInit({
  dsn: isSentryEnabled() ? UI_SENTRY_URL : undefined,
  environment: Flags.DB_ENV,
  release: Flags.GIT_COMMIT_HASH,
  // Sentry production defaults; the prior 1.0 tracesSampleRate amplified every
  // wrapped event callback during high-frequency UI work.
  tracesSampleRate: 0.1,
  initialScope: {
    extra: {},
  },
  denyUrls: [DEBUG_PAGE_REGEX],
  beforeSend(event) {
    if (DEBUG_PAGE_REGEX.test(String(event?.tags?.url))) {
      return null;
    }
    return event;
  },
  maxValueLength: 8192,
  // Sentry production default; a 5000-entry ring duplicates work on every
  // wrapped addEventListener / setTimeout / requestAnimationFrame.
  maxBreadcrumbs: 100,
  integrations: [
    thirdPartyErrorFilterIntegration({
      filterKeys: ["higharc/browser"],
      behaviour: "apply-tag-if-exclusively-contains-third-party-frames",
    }),
    PostHogIntegration(),
    rewriteFramesIntegration({
      iteratee(frame) {
        if (frame.filename) {
          frame.filename = decodeURI(frame.filename);
        }
        return frame;
      },
    }),
  ],
});

// Create the router
const router = createRouter({
  routeTree,
  context: { enabledFeatures: {}, enabledProducts: {}, flagsHydrated: false } satisfies AppRouterContext,
  defaultPreload: "intent",
  defaultOnCatch: (error) => {
    if (isChunkLoadError(error) && shouldAttemptChunkReload()) {
      reloadPageWithNotification("Detected chunk-load error in router defaultOnCatch");
    }
  },
  // Use plain URLSearchParams instead of TanStack's default JSON-based serializer.
  // The default serializer JSON-stringifies values, which wraps strings like "true"
  // and "50" in quotes (e.g., ie=%22true%22), corrupting URLs on every navigation.
  stringifySearch: (search: Record<string, unknown>): string => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value === undefined || value === null) {
        continue;
      }
      if (typeof value === "object") {
        params.set(key, JSON.stringify(value));
      } else {
        params.set(key, String(value));
      }
    }
    const str = params.toString();
    return str ? `?${str}` : "";
  },
  parseSearch: (searchStr: string): Record<string, string> => {
    const s = searchStr.startsWith("?") ? searchStr.substring(1) : searchStr;
    const params = new URLSearchParams(s);
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  },
});

// Register the router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
  interface StaticDataRouteOption {
    isPublicPage?: boolean;
  }
}

function SentryErrorFallback() {
  return (
    <HelmetProvider>
      <Helmet>
        <title>{getPageTitle("Error")}</title>
      </Helmet>
      <div>An error occurred. Please refresh the page.</div>
    </HelmetProvider>
  );
}

function renderApp(): void {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    return;
  }

  createRoot(rootElement).render(
    <StrictMode>
      <SentryErrorBoundary fallback={<SentryErrorFallback />}>
        <RouterProvider router={router} />
      </SentryErrorBoundary>
    </StrictMode>,
  );
}

async function init(): Promise<void> {
  if (isRenderDebugEnabled()) {
    try {
      await ensureReactScanScriptLoaded();
    } catch (error) {
      console.warn(error);
    }
  }

  renderApp();
}

void init();
